import React, { Fragment, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Error } from '../design/elements';
import FormField from './formField';
import {
  GroupTitle,
  GroupWrapper,
  RepeatedFormGroup,
  RepeatedFormTitle
} from './css';
import {
  fieldNumberOfSitesThatRequireWhiteGlove,
  keyDirectoryListingDetails,
  keyTotalNoOfSeats,
  keyWhiteGloveLocations,
  fieldStreetNumberServiceAddress,
  fieldStreetNameServiceAddress,
  fieldUnitNumberServiceAddress,
  fieldUnitTypeServiceAddress,
  fieldCityServiceAddress,
  fieldProvinceServiceAddress,
  fieldPostalCodeServiceAddress
} from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import stringInterpolation from '../utils/stringInterpolation';

const RepeatedForm = ({
  data,
  formErrors,
  formFields,
  orderData,
  setFormFields,
  isFormTouched,
  setIsFormTouched,
  isMultisiteOrder
}) => {
  const {
    name,
    key,
    formFields: contentFulFields,
    formTitle,
    hideTitle
  } = data;

  const localizedStrings = useLocalizedStringsContext();
  const {
    whiteGloveDetails: { errorMessage },
    modalForm: { fieldRequiredMessage }
  } = localizedStrings;
  const [formsList, setFormsList] = useState(formFields[key]);
  const [seatLimitError, setSeatLimitError] = useState('');
  const repeatedFormError = formErrors[key];
  const totalSeats =
    parseInt(orderData.officeSeats, 10) + parseInt(orderData.starterSeat, 10);
  const isDirectoryListing = key === keyDirectoryListingDetails;
  const isWhiteGloveLocations = key === keyWhiteGloveLocations;

  const getTotalSeats = () => {
    return formFields[key].reduce((acc, location) => {
      const value = location[keyTotalNoOfSeats];
      return value ? acc + Number(value) : acc;
    }, 0);
  };

  const getServiceAddress = () => {
    const serviceAddress = `${_.get(
      orderData,
      fieldStreetNumberServiceAddress,
      ''
    )} ${_.get(orderData, fieldStreetNameServiceAddress, '')} ${_.get(
      orderData,
      fieldUnitTypeServiceAddress,
      ''
    )} ${_.get(orderData, fieldUnitNumberServiceAddress, '')} ${_.get(
      orderData,
      fieldCityServiceAddress,
      ''
    )} ${_.get(orderData, fieldProvinceServiceAddress, '')} ${_.get(
      orderData,
      fieldPostalCodeServiceAddress,
      ''
    )}`;
    return serviceAddress;
  };

  const fieldsMap = useMemo(
    () =>
      contentFulFields.reduce((acc, { fields }) => {
        if (isWhiteGloveLocations && isMultisiteOrder)
          acc[fields.name] = { ...fields, disabled: true };
        else acc[fields.name] = { ...fields };
        return acc;
      }, {}),
    [contentFulFields]
  );

  const formObjectKeys = data.formFields.reduce(
    (acc, { fields: { name: formNameKey, value: defaultValue } }) => {
      acc[formNameKey] = defaultValue;
      return acc;
    },
    {}
  );

  useEffect(() => {
    const numberOfSites = formFields[fieldNumberOfSitesThatRequireWhiteGlove];
    if (isWhiteGloveLocations && numberOfSites) {
      const newValue = [];
      if (isMultisiteOrder) {
        newValue.push({
          address: getServiceAddress(),
          keyTotalNoOfSeats: totalSeats
        });
      } else {
        let diff = numberOfSites.value || 0;
        while (diff > 0) {
          newValue.push({ ...formObjectKeys });
          diff -= 1;
        }
      }
      setFormsList(newValue);
    }
  }, [formFields[fieldNumberOfSitesThatRequireWhiteGlove].value]);

  const handleFormField = (index, fieldKey, value) => {
    const fieldList = formsList;
    fieldList[index][fieldKey] = value;
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = fieldList;
    if (key === keyWhiteGloveLocations && fieldKey === keyTotalNoOfSeats) {
      const currentTotal = getTotalSeats();
      if (totalSeats < currentTotal) {
        setSeatLimitError(stringInterpolation(errorMessage, totalSeats));
        fieldList[index][fieldKey] = '';
      } else {
        setSeatLimitError('');
      }
    }
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
    setFormFields(updatedFormFields);
  };

  useEffect(() => {
    if (formsList.length > 0 && isWhiteGloveLocations && isMultisiteOrder) {
      handleFormField(0, 'address', getServiceAddress());
      handleFormField(0, keyTotalNoOfSeats, totalSeats);
    }
  }, [formsList]);

  return (
    <GroupWrapper>
      {!hideTitle && <GroupTitle>{name}</GroupTitle>}
      {formsList &&
        formsList.map((form, index) => {
          const formsListKey = `form${index}`;
          return (
            <Fragment key={formsListKey}>
              <RepeatedFormGroup>
                <RepeatedFormTitle>
                  {isDirectoryListing
                    ? form.title
                    : `${formTitle} ${index + 1}`}
                </RepeatedFormTitle>

                {Object.keys(form).map(formKey => {
                  const fieldKey = `repeatedForm${key}${formKey}${index}`;
                  const currentValue = formsList[index][formKey];
                  const fieldData = {
                    ...fieldsMap[formKey],
                    name: formKey,
                    sysId: fieldKey,
                    value: currentValue,
                    onChange: ({ target: { value } }) =>
                      handleFormField(index, formKey, value)
                  };
                  const error =
                    repeatedFormError &&
                    !currentValue &&
                    fieldsMap[formKey].required
                      ? fieldRequiredMessage
                      : '';
                  return (
                    <Fragment key={fieldKey}>
                      <FormField
                        key={`field${formKey}${fieldKey}`}
                        data={fieldData}
                      />
                      {error && <Error text={error} />}
                      {formKey === keyTotalNoOfSeats && seatLimitError && (
                        <Error text={seatLimitError} />
                      )}
                    </Fragment>
                  );
                })}
              </RepeatedFormGroup>
            </Fragment>
          );
        })}
    </GroupWrapper>
  );
};

RepeatedForm.propTypes = {
  data: PropTypes.shape({
    key: PropTypes.string.isRequired,
    formFields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    repeatBasedOnBackendField: PropTypes.string,
    formTitle: PropTypes.string,
    hideTitle: PropTypes.bool
  }).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.any),
  formFields: PropTypes.objectOf(PropTypes.any).isRequired,
  orderData: PropTypes.objectOf(PropTypes.any).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired,
  isMultisiteOrder: PropTypes.bool.isRequired
};

RepeatedForm.defaultProps = {
  formErrors: {}
};

export default RepeatedForm;
